import { useState, useEffect } from 'react';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft,
  BarChart3, 
  Mail, 
  MailOpen, 
  MousePointerClick, 
  AlertTriangle,
  UserMinus,
  TrendingUp,
  Send,
  Loader2,
  Users,
  RefreshCw
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function NewsletterAnalyticsPage() {
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  
  const getToken = () => localStorage.getItem('shrt_token');

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/newsletter/analytics/overview`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      setAnalytics(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Analytics');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('de-CH', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  const totals = analytics?.totals || {};
  const rates = analytics?.rates || {};
  const subscribers = analytics?.subscribers || {};

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin/newsletter-system" className="text-muted-foreground hover:text-foreground">
              <ArrowLeft size={20} />
            </Link>
            <div className="flex items-center gap-3">
              <BarChart3 className="text-primary" size={28} />
              <div>
                <h1 className="font-['Oswald'] text-2xl font-bold text-foreground uppercase tracking-tight">
                  Newsletter Analytics
                </h1>
                <p className="text-sm text-muted-foreground">
                  Übersicht aller Newsletter-Statistiken
                </p>
              </div>
            </div>
          </div>
          
          <Button variant="outline" onClick={fetchAnalytics} className="gap-2 rounded-none">
            <RefreshCw size={16} />
            Aktualisieren
          </Button>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-card border border-border p-6 text-center">
            <Send className="mx-auto mb-3 text-blue-500" size={28} />
            <p className="text-3xl font-bold text-foreground">{totals.sent || 0}</p>
            <p className="text-xs text-muted-foreground uppercase mt-1">E-Mails gesendet</p>
          </div>
          
          <div className="bg-card border border-border p-6 text-center">
            <MailOpen className="mx-auto mb-3 text-green-500" size={28} />
            <p className="text-3xl font-bold text-foreground">{totals.opened || 0}</p>
            <p className="text-xs text-muted-foreground uppercase mt-1">Geöffnet</p>
            <p className="text-lg font-semibold text-green-500 mt-1">{rates.open_rate || 0}%</p>
          </div>
          
          <div className="bg-card border border-border p-6 text-center">
            <MousePointerClick className="mx-auto mb-3 text-purple-500" size={28} />
            <p className="text-3xl font-bold text-foreground">{totals.clicked || 0}</p>
            <p className="text-xs text-muted-foreground uppercase mt-1">Klicks</p>
            <p className="text-lg font-semibold text-purple-500 mt-1">{rates.click_rate || 0}%</p>
          </div>
          
          <div className="bg-card border border-border p-6 text-center">
            <AlertTriangle className="mx-auto mb-3 text-orange-500" size={28} />
            <p className="text-3xl font-bold text-foreground">{totals.bounced || 0}</p>
            <p className="text-xs text-muted-foreground uppercase mt-1">Bounces</p>
            <p className="text-lg font-semibold text-orange-500 mt-1">{rates.bounce_rate || 0}%</p>
          </div>
        </div>

        {/* Subscriber Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-green-500/10 border border-green-500/30 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-400 uppercase tracking-wider">Aktive Abonnenten</p>
                <p className="text-3xl font-bold text-green-500 mt-2">{subscribers.total || 0}</p>
              </div>
              <Users className="text-green-500" size={32} />
            </div>
          </div>
          
          <div className="bg-orange-500/10 border border-orange-500/30 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-orange-400 uppercase tracking-wider">Bounced</p>
                <p className="text-3xl font-bold text-orange-500 mt-2">{subscribers.bounced || 0}</p>
              </div>
              <AlertTriangle className="text-orange-500" size={32} />
            </div>
          </div>
          
          <div className="bg-card border border-border p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground uppercase tracking-wider">Abgemeldet (gesamt)</p>
                <p className="text-3xl font-bold text-foreground mt-2">{totals.unsubscribed || 0}</p>
              </div>
              <UserMinus className="text-muted-foreground" size={32} />
            </div>
          </div>
        </div>

        {/* Campaign Stats */}
        <div className="bg-card border border-border p-6">
          <h2 className="font-semibold mb-4 flex items-center gap-2">
            <Mail size={18} />
            Kampagnen-Übersicht
          </h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 bg-muted/20 rounded">
              <p className="text-sm text-muted-foreground">Gesamt Kampagnen</p>
              <p className="text-2xl font-bold">{analytics?.campaigns?.total || 0}</p>
            </div>
            <div className="p-4 bg-muted/20 rounded">
              <p className="text-sm text-muted-foreground">Gesendet</p>
              <p className="text-2xl font-bold">{analytics?.campaigns?.sent || 0}</p>
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Recent Bounces */}
          <div className="bg-card border border-border p-6">
            <h2 className="font-semibold mb-4 flex items-center gap-2">
              <AlertTriangle size={18} className="text-orange-500" />
              Letzte Bounces
            </h2>
            {(analytics?.recent_bounces || []).length === 0 ? (
              <p className="text-muted-foreground text-sm">Keine Bounces vorhanden</p>
            ) : (
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {(analytics?.recent_bounces || []).map((bounce) => (
                  <div key={bounce.id} className="p-3 bg-muted/20 rounded text-sm">
                    <div className="flex items-center justify-between">
                      <span className="font-medium truncate">{bounce.email}</span>
                      <span className={`text-xs px-2 py-0.5 ${
                        bounce.bounce_type === 'hard' 
                          ? 'bg-red-500/20 text-red-400' 
                          : 'bg-yellow-500/20 text-yellow-400'
                      }`}>
                        {bounce.bounce_type === 'hard' ? 'Hard Bounce' : 'Soft Bounce'}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {formatDate(bounce.created_at)}
                    </p>
                  </div>
                ))}
              </div>
            )}
            <Link to="/admin/newsletter-system/bounces" className="block mt-4">
              <Button variant="outline" size="sm" className="w-full rounded-none">
                Alle Bounces anzeigen
              </Button>
            </Link>
          </div>

          {/* Recent Unsubscribes */}
          <div className="bg-card border border-border p-6">
            <h2 className="font-semibold mb-4 flex items-center gap-2">
              <UserMinus size={18} className="text-red-500" />
              Letzte Abmeldungen
            </h2>
            {(analytics?.recent_unsubscribes || []).length === 0 ? (
              <p className="text-muted-foreground text-sm">Keine Abmeldungen vorhanden</p>
            ) : (
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {(analytics?.recent_unsubscribes || []).map((unsub) => (
                  <div key={unsub.id} className="p-3 bg-muted/20 rounded text-sm">
                    <span className="font-medium">{unsub.email}</span>
                    <p className="text-xs text-muted-foreground mt-1">
                      Abgemeldet am {formatDate(unsub.unsubscribed_at)}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
